/**
 * Single client-side source of truth for the file-upload allowlist —
 * mirrors `ALLOWED_UPLOAD_EXTENSIONS` in
 * `flowdesk-api/src/modules/files/files.service.js` (the server stays
 * authoritative; this is a UX shortcut so a rejected file gets an instant
 * friendly toast instead of a failed HTTP round-trip).
 *
 * Used by BOTH the Files & Links surfaces and the per-task / per-Space
 * attachment cell, so what you can attach inside a Space is exactly what
 * the Files & Links page accepts — the two never drift apart.
 */
export const ALLOWED_UPLOAD_EXTENSIONS = new Set([
  // Office documents
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  // Images
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
])

/** `accept` attribute for an upload `<input type="file">` — filters the OS
 *  file picker to the allowed types before the user even submits. */
export const UPLOAD_ACCEPT_ATTR = [...ALLOWED_UPLOAD_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(',')

/** Human-readable comma-separated extension list for helper text and the
 *  rejection toast — one string so the wording can never drift from the
 *  actual allowlist. Produces ".pdf, .doc, .docx, …, .svg". */
export const UPLOAD_EXTENSIONS_TEXT = [...ALLOWED_UPLOAD_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(', ')

/** Client-side upper bound for uploads — matches the server's absolute
 *  MAX_UPLOAD_CEILING_MB (see files.service.js). The operator can LOWER
 *  the effective cap via the `uploads.max_size_mb` platform setting; the
 *  server enforces that lower value authoritatively. This constant is
 *  only a "fail-fast" pre-check so a user picking an obviously huge file
 *  doesn't waste bandwidth. Bug 0013349: was 100 MB, which blocked a
 *  600 MB upload client-side even when the operator had raised the cap
 *  to 700 MB. */
export const MAX_UPLOAD_BYTES = 2048 * 1024 * 1024
export const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / 1024 / 1024

/** True when the file's extension is in the allowlist. Client-side
 *  pre-check before any upload. */
export function isAllowedUploadFile(file) {
  if (!file?.name) return false
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return ALLOWED_UPLOAD_EXTENSIONS.has(ext)
}
